import ClipDurationButtonList from '@src/components/ClipProducer/ClipDurationButtonList';
import Spinner from '@src/components/common/Spinner';
import { formatCandidateDuration } from '@src/services/clipSearch/searchReply';
import ArrowUp from '@src/svgs/ArrowUp';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled, { css } from 'styled-components';

import { splitAnswerQuotes } from './answerQuotes';

import type { CandidateView } from './useCandidateHighlights';
import type { ClipChatMessage, ClipChatMode } from './useClipSearchChat';

/**
 * Short, and few: these are a nudge, not the menu — the whole feature is "say
 * it in your own words", and a long list of cards out-weighs the composer and
 * becomes the thing the eye lands on. Ask carries more because it opens first
 * and its range is the less obvious of the two: two that reduce the whole
 * story, two that go looking for the interesting part of it, and one that asks
 * for a judgment about where the story would play. An answer's quotations are
 * clickable, so even the last one ends somewhere the user can act on.
 */
const EXAMPLE_PROMPTS: Record<ClipChatMode, string[]> = {
  clips: ['The most quotable moment', 'Find me a hook'],
  ask: [
    'Summarize this story',
    'What are the main themes?',
    'What’s the most surprising thing said?',
    'What’s the emotional turning point?',
    'Suggest clips for LinkedIn',
  ],
};

const EMPTY_BODY: Record<ClipChatMode, string> = {
  clips: 'I’ll read the whole transcript and mark the best options.',
  ask: 'I’ll read the whole transcript and answer.',
};

/** Roughly five lines — past that the composer stops eating the reply. */
const INPUT_MAX_HEIGHT = 116;

const INPUT_PLACEHOLDER: Record<ClipChatMode, string> = {
  clips: 'Describe the clip you want…',
  ask: 'Ask about this story…',
};

export interface FindClipsPanelProps {
  messages: ClipChatMessage[];
  isSearching: boolean;
  mode: ClipChatMode;
  onModeChange: (mode: ClipChatMode) => void;
  targetDuration: number | null;
  onTargetDurationChange: (seconds: number | null) => void;
  onSendMessage: (text: string) => void;
  onCancelSearch: () => void;

  candidates: CandidateView[];
  totalCandidates: number;
  expanded: boolean;
  onToggleExpanded: () => void;
  onSelectCandidate: (key: string | null) => void;
  /** Scrolls the transcript to the candidate and seeks the docked player. */
  onRevealCandidate: (candidate: CandidateView) => void;
  onSaveCandidate: (candidate: CandidateView) => void;
  /** Keys currently saving, and keys already saved, for the card's state. */
  savingKeys: ReadonlySet<string>;
  savedKeys: ReadonlySet<string>;
  /** Marks the candidate's words in the transcript, without cutting a clip. */
  onHighlightCandidate: (candidate: CandidateView) => void;
  highlightedKeys: ReadonlySet<string>;

  /**
   * Where a quotation in an answer sits in the transcript, or null when it
   * can't be placed. Must be stable — it is a render-time lookup.
   */
  locateQuote: (quote: string) => number | null;
  onJumpToQuote: (index: number) => void;

  showOnly: boolean;
  onShowOnlyChange: (next: boolean) => void;
  /** True while the docked player is auditioning the candidates. */
  isPlaying: boolean;
  onTogglePlayAll: () => void;
}

/**
 * The Co-Producer tab's chat (PLAT-11806) — Find Clips and Ask a Question.
 * The file keeps its original name; the tab is what was renamed.
 *
 * Presentation only — every piece of state is owned by the host
 * (`useClipSearchChat` for the conversation, `useCandidateHighlights` for the
 * selection and the transcript props), so this component can be rendered from
 * a test with plain objects.
 */
const FindClipsPanel: React.FC<FindClipsPanelProps> = props => {
  const {
    messages,
    isSearching,
    mode,
    onModeChange,
    targetDuration,
    onTargetDurationChange,
    onSendMessage,
    onCancelSearch,
    candidates,
    totalCandidates,
    expanded,
    onToggleExpanded,
    onSelectCandidate,
    onRevealCandidate,
    onSaveCandidate,
    savingKeys,
    savedKeys,
    onHighlightCandidate,
    highlightedKeys,
    locateQuote,
    onJumpToQuote,
    showOnly,
    onShowOnlyChange,
    isPlaying,
    onTogglePlayAll,
  } = props;

  const [inputValue, setInputValue] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const newestTurnRef = useRef<HTMLDivElement>(null);

  // The composer is the call to action, so it opens focused. Guarded per
  // CLAUDE.md pitfall 10: pulling focus off the renderer iframe dispatches a
  // FORCED_REFRESH that can revert an in-flight canvas edit.
  useEffect(() => {
    if (document.activeElement instanceof HTMLIFrameElement) return;
    inputRef.current?.focus();
  }, []);

  /**
   * Follow the conversation as it grows — and ONLY as it grows.
   *
   * Keyed on the message count, never on `candidates`: selecting a card gives
   * `visibleCandidates` a new identity, which this effect read as "something
   * arrived" and answered by scrolling, carrying the card the user had just
   * clicked (and its Save as Clip button) out of view. That is CLAUDE.md
   * pitfall 18 — a freshly-built array as an effect dep.
   *
   * Lands the newest turn's FIRST line at the top of the panel, not its last.
   * Scrolling to `scrollHeight` put the action row against the bottom edge and
   * pushed the top-ranked option above the fold — the one result the user most
   * wants to see, hidden by the scroll meant to reveal it. A turn is taller
   * than the panel (reply + hint + three cards + actions), so bottom-anchoring
   * always loses its head.
   *
   * Measured against the container's own rect rather than `offsetTop`, whose
   * `offsetParent` is not this scroller, and scrolled on the panel's own
   * element rather than via `scrollIntoView`, which walks every scrollable
   * ancestor and would drag the transcript pane along with it.
   */
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const newest = newestTurnRef.current;
    if (!newest) {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
      return;
    }
    const offset =
      newest.getBoundingClientRect().top - el.getBoundingClientRect().top;
    el.scrollTo({ top: el.scrollTop + offset, behavior: 'smooth' });
  }, [messages.length]);

  /**
   * The composer starts one line tall and grows with what is typed, up to a
   * cap — the panel is a column a few hundred pixels wide, so a permanently
   * three-row box spends the reply's space on emptiness. Measured in a layout
   * effect so the box is the right height in the same frame as the text.
   */
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, INPUT_MAX_HEIGHT)}px`;
  }, [inputValue]);

  const submit = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isSearching) return;
      onSendMessage(trimmed);
      setInputValue('');
    },
    [isSearching, onSendMessage],
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      submit(inputValue);
    },
    [inputValue, submit],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        submit(inputValue);
      }
    },
    [inputValue, submit],
  );

  const hasConversation = messages.length > 0;

  return (
    <Panel data-testid="find-clips-panel">
      <Scroll ref={scrollRef}>
        {!hasConversation && (
          <EmptyState>
            <EmptyBody>{EMPTY_BODY[mode]}</EmptyBody>
            <Examples>
              {EXAMPLE_PROMPTS[mode].map(example => (
                <ExampleButton
                  key={example}
                  type="button"
                  onClick={() => submit(example)}
                  disabled={isSearching}
                >
                  {example}
                </ExampleButton>
              ))}
            </Examples>
          </EmptyState>
        )}

        {messages.map((message, messageIndex) => (
          <React.Fragment key={message.id}>
            {message.role === 'user' ? (
              <UserBubble>{message.content}</UserBubble>
            ) : (
              <AssistantText
                ref={
                  messageIndex === messages.length - 1
                    ? newestTurnRef
                    : undefined
                }
                $isError={message.kind === 'error'}
              >
                {message.kind === 'answer' ? (
                  <AnswerBody
                    text={message.content}
                    locateQuote={locateQuote}
                    onJumpToQuote={onJumpToQuote}
                    // Placing a quote reads the whole transcript, so it waits
                    // until the answer has stopped growing — only the last
                    // message can still be streaming.
                    linkQuotes={
                      !isSearching || messageIndex < messages.length - 1
                    }
                  />
                ) : (
                  message.content
                )}
              </AssistantText>
            )}

            {message.kind === 'results' && candidates.length > 0 && (
              <>
                <ResultsHint>Click one to play it below.</ResultsHint>
                <CandidateList>
                  {candidates.map(candidate => {
                    const isSaved = savedKeys.has(candidate.key);
                    const isSaving = savingKeys.has(candidate.key);
                    const isHighlighted = highlightedKeys.has(candidate.key);
                    return (
                      <CandidateRow
                        key={candidate.key}
                        $selected={candidate.isSelected}
                        data-testid={`clip-candidate-${candidate.rank}`}
                      >
                        <RankBadge
                          $selected={candidate.isSelected}
                          $saved={isSaved}
                        >
                          {isSaved ? '✓' : candidate.rank}
                        </RankBadge>
                        <CandidateBody>
                          <CandidateQuote
                            type="button"
                            onClick={() => {
                              onSelectCandidate(candidate.key);
                              onRevealCandidate(candidate);
                            }}
                          >
                            {candidate.fragment.text}
                          </CandidateQuote>
                          <CandidateMeta>
                            {candidate.fragment.speakerName && (
                              <SpeakerName>
                                {candidate.fragment.speakerName}
                              </SpeakerName>
                            )}
                            <Duration>
                              {formatCandidateDuration(
                                candidate.fragment.duration,
                              )}
                            </Duration>
                            <MetaSpacer />
                            {isHighlighted ? (
                              <HighlightedLabel>Highlighted</HighlightedLabel>
                            ) : (
                              <SaveButton
                                type="button"
                                $primary={false}
                                onClick={() => onHighlightCandidate(candidate)}
                              >
                                Highlight
                              </SaveButton>
                            )}
                            {isSaved ? (
                              <SavedLabel>Saved to Clips</SavedLabel>
                            ) : (
                              <SaveButton
                                type="button"
                                $primary={candidate.isSelected}
                                disabled={isSaving}
                                onClick={() => onSaveCandidate(candidate)}
                              >
                                {isSaving ? 'Saving…' : 'Save as Clip'}
                              </SaveButton>
                            )}
                          </CandidateMeta>
                        </CandidateBody>
                      </CandidateRow>
                    );
                  })}
                </CandidateList>

                <ActionRow>
                  <ActionButton type="button" onClick={onTogglePlayAll}>
                    {isPlaying
                      ? 'Pause'
                      : `Play ${candidates.length > 1 ? `All ${candidates.length}` : 'It'}`}
                  </ActionButton>
                  <ActionButton
                    type="button"
                    $active={showOnly}
                    onClick={() => onShowOnlyChange(!showOnly)}
                  >
                    Show Only These
                  </ActionButton>
                  {totalCandidates > candidates.length && (
                    <ActionButton type="button" onClick={onToggleExpanded}>
                      {`Show ${totalCandidates - candidates.length} More`}
                    </ActionButton>
                  )}
                  {expanded && (
                    <ActionButton type="button" onClick={onToggleExpanded}>
                      Show Fewer
                    </ActionButton>
                  )}
                </ActionRow>
              </>
            )}
          </React.Fragment>
        ))}

        {isSearching && (
          <SearchingRow>
            <Spinner size="sm" />
            <CancelButton type="button" onClick={onCancelSearch}>
              Stop
            </CancelButton>
          </SearchingRow>
        )}
      </Scroll>

      <Composer onSubmit={handleSubmit}>
        {/* What the next sentence will do, set before it is typed — so the
            panel says which of the two things it will spend the wait on. */}
        <ModeToggle role="group" aria-label="What to do with your message">
          <button
            type="button"
            className={mode === 'ask' ? 'active' : ''}
            aria-pressed={mode === 'ask'}
            onClick={() => onModeChange('ask')}
            data-testid="chat-mode-ask"
          >
            Ask a Question
          </button>
          <button
            type="button"
            className={mode === 'clips' ? 'active' : ''}
            aria-pressed={mode === 'clips'}
            onClick={() => onModeChange('clips')}
            data-testid="chat-mode-clips"
          >
            Find a Segment
          </button>
        </ModeToggle>

        {/* Length only means something when clips come back. */}
        {mode === 'clips' && (
          <DurationRow>
            <SectionLabel as="span">Length</SectionLabel>
            <AnyButton
              type="button"
              $active={targetDuration === null}
              onClick={() => onTargetDurationChange(null)}
            >
              Any
            </AnyButton>
            <ClipDurationButtonList
              selectedDuration={targetDuration}
              setSelectedDuration={onTargetDurationChange}
              size="sm"
            />
          </DurationRow>
        )}

        <InputRow>
          <VisuallyHiddenLabel htmlFor="find-clips-input">
            {mode === 'clips'
              ? 'Describe the clip you want'
              : 'Ask about this story'}
          </VisuallyHiddenLabel>
          <Input
            ref={inputRef}
            id="find-clips-input"
            value={inputValue}
            placeholder={INPUT_PLACEHOLDER[mode]}
            rows={1}
            onChange={e => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isSearching}
          />
          <SendButton
            type="submit"
            aria-label="Send"
            disabled={isSearching || !inputValue.trim()}
          >
            <ArrowUp
              width="14"
              height="14"
              viewBox="0 0 24 24"
              d="M12 19V5M5 12l7-7 7 7"
              strokeWidth="2.4"
              strokeColor="currentColor"
            />
          </SendButton>
        </InputRow>
      </Composer>
    </Panel>
  );
};

export default FindClipsPanel;

/**
 * An answer's prose, with every quotation the transcript can place rendered as
 * a jump to it. A quote that can't be placed stays plain text — a link that
 * lands somewhere arbitrary is worse than no link.
 */
const AnswerBody: React.FC<{
  text: string;
  locateQuote: (quote: string) => number | null;
  onJumpToQuote: (index: number) => void;
  linkQuotes: boolean;
}> = ({ text, locateQuote, onJumpToQuote, linkQuotes }) => {
  const parts = useMemo(
    () =>
      splitAnswerQuotes(text).map(part => ({
        text: part.text,
        index: linkQuotes && part.quote ? locateQuote(part.quote) : null,
      })),
    [text, locateQuote, linkQuotes],
  );

  return (
    <>
      {parts.map((part, i) => {
        const target = part.index;
        if (target == null) {
          // eslint-disable-next-line react/no-array-index-key
          return <React.Fragment key={i}>{part.text}</React.Fragment>;
        }
        return (
          <QuoteLink
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            type="button"
            onClick={() => onJumpToQuote(target)}
            title="Jump to this in the transcript"
          >
            {part.text}
          </QuoteLink>
        );
      })}
    </>
  );
};

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`;

const Scroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const EmptyState = styled.div`
  /* Top of the panel, where the first reply will appear — so the examples sit
     where the conversation starts instead of floating mid-column with empty
     space above and below them. Replaced by turns, never moved. */
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const EmptyBody = styled.p`
  margin: 0;
  font-size: var(--text-xs);
  line-height: 1.5;
  color: var(--text-muted);
`;

const SectionLabel = styled.h3`
  margin: 6px 0 0;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
`;

const Examples = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ExampleButton = styled.button`
  text-align: left;
  padding: 7px 10px;
  border-radius: var(--radius-input);
  /* Ghost, not a filled card: a nudge that sits behind the composer in the
     visual order instead of competing with it. */
  background: transparent;
  border: 1px solid var(--border-subtle);
  color: var(--text-secondary);
  font-family: inherit;
  font-size: var(--text-xs);
  line-height: 1.4;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: var(--surface-elevated);
    color: var(--foreground);
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

/**
 * Your own words, at the same reading weight as the reply and the transcript —
 * the bubble's background is what marks it as yours, so brightness does not
 * have to. Sized with the thread rather than a step below it, so a question
 * and its answer read as one exchange.
 */
const UserBubble = styled.div`
  align-self: flex-end;
  max-width: 85%;
  padding: 8px 12px;
  border-radius: 14px 14px 4px 14px;
  background: var(--surface-elevated);
  font-size: var(--text-sm);
  line-height: 1.5;
  color: var(--text-secondary);
`;

/**
 * Deliberately the transcript's own reading treatment — `--text-secondary` at
 * `--text-sm`, matching `WordLight` / `colorByState`'s default in
 * `TranscriptionText`. An answer is prose read beside the transcript, in the
 * next column over, so anything brighter or smaller reads as chrome about the
 * story rather than as more of the story.
 */
const AssistantText = styled.div<{ $isError: boolean }>`
  font-size: var(--text-sm);
  line-height: 1.6;
  /* An answer arrives as prose with its own paragraph breaks; every other
     assistant line is a single sentence, so this only shows up where it is
     wanted. */
  white-space: pre-wrap;
  color: ${props =>
    props.$isError ? 'var(--destructive)' : 'var(--text-secondary)'};
`;

/**
 * A quotation inside the prose, not a button on top of it: it keeps the
 * paragraph's font and color and wraps with the text, and only the dotted
 * underline says it can be clicked.
 */
const QuoteLink = styled.button`
  display: inline;
  margin: 0;
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;
  text-decoration: underline;
  text-decoration-style: dotted;
  text-decoration-color: var(--text-muted);
  text-underline-offset: 3px;

  &:hover {
    color: var(--foreground);
    text-decoration-color: var(--primary);
  }
`;

const ResultsHint = styled.div`
  font-size: 11px;
  color: var(--text-muted);
`;

const CandidateList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const CandidateRow = styled.div<{ $selected: boolean }>`
  display: flex;
  gap: 10px;
  padding: 10px;
  border-radius: var(--radius-card);
  background: var(--surface-elevated);
  border: 1px solid
    ${props => (props.$selected ? 'var(--primary)' : 'var(--border)')};
`;

const RankBadge = styled.span<{ $selected: boolean; $saved: boolean }>`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: var(--radius-input);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-semi-bold);

  ${props => {
    if (props.$saved) {
      return css`
        background: var(--content-clips-bg);
        color: var(--content-clips-text);
      `;
    }
    return props.$selected
      ? css`
          background: var(--foreground);
          color: var(--background);
        `
      : css`
          background: var(--border);
          color: var(--foreground);
        `;
  }}
`;

const CandidateBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const CandidateQuote = styled.button`
  display: -webkit-box;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  padding: 0;
  font-family: inherit;
  font-size: var(--text-xs);
  line-height: 1.45;
  color: var(--foreground);
  cursor: pointer;
  /* Three lines is enough to recognise a passage; the transcript has the rest. */
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;

  /* Clicking plays this passage below, so say so on hover rather than leaving
     the whole card looking inert. */
  &:hover {
    color: var(--primary);
  }
`;

const CandidateMeta = styled.div`
  margin-top: 7px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SpeakerName = styled.span`
  font-size: 11px;
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Duration = styled.span`
  font-size: 11px;
  font-weight: var(--font-weight-semi-bold);
  color: var(--foreground);
  white-space: nowrap;
`;

const MetaSpacer = styled.div`
  flex: 1;
`;

const SavedLabel = styled.span`
  font-size: 11px;
  color: var(--content-clips-text);
  white-space: nowrap;
`;

/** The highlight's own color family, so the row echoes the transcript wash. */
const HighlightedLabel = styled.span`
  font-size: 11px;
  color: var(--content-saved-segments-text);
  white-space: nowrap;
`;

const SaveButton = styled.button<{ $primary: boolean }>`
  height: var(--btn-h-sm, 28px);
  padding: 0 10px;
  border-radius: var(--radius-button);
  font-family: inherit;
  font-size: var(--text-xs);
  cursor: pointer;
  white-space: nowrap;

  ${props =>
    props.$primary
      ? css`
          background: var(--primary);
          border: 1px solid var(--primary);
          color: var(--background);
          font-weight: var(--font-weight-semi-bold);

          &:hover:not(:disabled) {
            background: var(--primary-hover);
          }
        `
      : css`
          background: transparent;
          border: 1px solid var(--border);
          color: var(--foreground);

          &:hover:not(:disabled) {
            background: var(--surface-elevated-hover);
          }
        `}

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const ActionButton = styled.button<{ $active?: boolean }>`
  height: var(--btn-h-sm, 28px);
  padding: 0 10px;
  border-radius: var(--radius-button);
  background: ${props =>
    props.$active ? 'var(--surface-elevated)' : 'transparent'};
  border: 1px solid var(--border);
  color: var(--foreground);
  font-family: inherit;
  font-size: var(--text-xs);
  cursor: pointer;

  &:hover {
    background: var(--surface-elevated-hover);
  }
`;

const SearchingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CancelButton = styled.button`
  height: 24px;
  padding: 0 10px;
  border-radius: var(--radius-button);
  background: transparent;
  border: 1px solid var(--border);
  color: var(--text-secondary);
  font-family: inherit;
  font-size: var(--text-xs);
  cursor: pointer;
`;

/**
 * Pinned to the bottom, always, in every state.
 *
 * An earlier pass centred this as a "new thread" hero and let it drop to the
 * bottom on the first search. It filled the blank panel nicely and then lurched
 * the moment you used it — the control you were typing into moved out from
 * under you. A composer that never moves is worth more than one that is
 * briefly better positioned, so the empty state fills the space above instead
 * (see `EmptyState`) and this stays put.
 */
const Composer = styled.form`
  flex-shrink: 0;
  border-top: 1px solid var(--border);
  background: var(--surface);
  padding: 8px 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

/**
 * Ask a Question | Find a Segment. The segmented-control idiom (`ViewToggle`,
 * `AudioModeSegmentedToggle`) with a neutral active state — choosing what the
 * composer does is navigation, not a visual/creative control, so no gold
 * (design-system.md).
 */
const ModeToggle = styled.div`
  display: flex;
  background: var(--surface-elevated);
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 2px;
  gap: 2px;

  button {
    flex: 1;
    border: 0;
    background: transparent;
    padding: 3px 8px;
    border-radius: 4px;
    font-family: inherit;
    font-size: var(--text-xs);
    font-weight: var(--font-weight-medium);
    color: var(--text-secondary);
    cursor: pointer;
    transition:
      background-color 120ms ease,
      color 120ms ease;
  }

  button:hover:not(.active) {
    color: var(--foreground);
  }

  button.active {
    background: var(--background);
    color: var(--foreground);
  }
`;

const DurationRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 0;
  /* One line, always. This row sitting above the composer is what pushed the
     input below the fold in a 360px column. */
  flex-wrap: nowrap;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  ${SectionLabel} {
    margin: 0;
    flex-shrink: 0;
  }

  /* ClipDurationButtonList carries a margin-top for its other hosts; here it
     would drop the chips below "Any". */
  & > :last-child {
    margin-top: 0;
  }
`;

/**
 * "Any" is the no-target state — the producer's own default 7–120s window.
 * It sits beside the shared duration chips rather than inside them so
 * `ClipDurationButtonList` stays the single owner of the numeric options.
 */
const AnyButton = styled.button<{ $active: boolean }>`
  height: 28px;
  padding: 0 10px;
  border-radius: var(--radius-input);
  font-family: inherit;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  background: ${props =>
    props.$active ? 'var(--accent-tint)' : 'var(--surface)'};
  border: 1px solid
    ${props => (props.$active ? 'var(--accent)' : 'var(--border)')};
  color: ${props => (props.$active ? 'var(--accent)' : 'var(--foreground)')};
`;

const InputRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 6px;
  padding: 5px 6px 5px 10px;
  border-radius: var(--radius-card);
  background: var(--background);
  /* Deliberately constant: this reads as the feature whether or not results
     are on screen, and a box that resizes when you use it is the jump this
     layout exists to avoid. */
  border: 1px solid var(--border-elevated);
  transition: border-color var(--transition-fast) ease;

  &:focus-within {
    border-color: var(--primary);
  }
`;

const VisuallyHiddenLabel = styled.label`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
`;

const Input = styled.textarea`
  flex: 1;
  min-width: 0;
  /* Height is set from the content in a layout effect; this is the ceiling
     for the case where that has not run yet. */
  max-height: ${INPUT_MAX_HEIGHT}px;
  overflow-y: auto;
  resize: none;
  background: transparent;
  border: none;
  outline: none;
  font-family: inherit;
  /* The placeholder is the instruction, so it reads as a prompt, not a
     caption — at one constant size. */
  font-size: var(--text-sm);
  line-height: 1.5;
  padding: 4px 0;
  /* What you type is the same text that lands in the bubble a moment later,
     so it is the same colour — otherwise sending visibly re-shades it. The
     placeholder stays a step down, which is the only thing separating a hint
     from a real value. */
  color: var(--text-secondary);

  &::placeholder {
    color: var(--text-muted);
  }
`;

const SendButton = styled.button`
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  border-radius: var(--radius-input);
  background: var(--primary);
  border: none;
  color: var(--background);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:disabled {
    background: var(--surface-elevated);
    color: var(--text-muted);
    cursor: default;
  }
`;
